import React, { useCallback, useRef, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';

import { SINGAPORE_L2 } from 'src/common/constants';
import { CountryCodes } from 'src/common/enums';
import useCountryConfig from 'src/common/hooks/useCountryConfig';
import { HierarchicalLocation } from 'src/modules/Profile/graphql/hierarchicalLocations';

import { buildLocationWithLabel, HierarchicalLocationSelect } from '.';

type PopularHierarchicalLocationSelectProps = {
  value?: HierarchicalLocation;
  onChange: (event: HierarchicalLocation | undefined) => void;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  error?: React.ReactNode;
  countryCode: CountryCodes;
  /** See src/common/testIds.ts. Forwarded to HierarchicalLocationSelect. */
  testIdPrefix?: string;
};

export const PopularHierarchicalLocationSelect = ({
  value,
  onChange,
  onBlur,
  error,
  countryCode,
  testIdPrefix,
}: PopularHierarchicalLocationSelectProps) => {
  const location = value;
  const countryConfig = useCountryConfig();
  const intl = useIntl();

  const [citySearchTerm, setCitySearchTerm] = useState('');
  const onChangeRef = useRef(onChange);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (location) {
        onChangeRef.current(undefined);
        setCitySearchTerm(e.key);
      }
    },
    [location]
  );

  const handleCitySelected = (selectedLocation: HierarchicalLocation) => {
    onChangeRef.current(selectedLocation);
    setCitySearchTerm(buildLocationWithLabel(selectedLocation).label);
  };

  const clearSelectedLocation = () => {
    onChangeRef.current(undefined);
  };

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const term = e.target.value.trim();
      if (location && (term === '' || !location.label?.startsWith(term))) {
        clearSelectedLocation();
      }
    },
    [location]
  );

  return (
    <HierarchicalLocationSelect
      label={intl.formatMessage(
        defineMessage({
          id: 'enter.a.city.location',
          defaultMessage: 'Enter your location (City & Country)',
        })
      )}
      searchLocationLevels={countryConfig.searchLevelsUserLocation}
      autoSelectOptionIds={autoSelectOptionId(countryCode)}
      countryCode={countryCode}
      value={citySearchTerm}
      error={error}
      testIdPrefix={testIdPrefix}
      onKeyPress={handleKeyPress}
      optionProps={{
        onOptionClick: (_, location) => handleCitySelected(location),
        onSelectOption: (location) => {
          handleCitySelected(location[0]);
        },
      }}
      onBlur={onBlur}
      onInputChange={handleInputChange}
      onClearSelected={clearSelectedLocation}
    />
  );
};

const autoSelectOptionId = (selectedCountry: CountryCodes): string[] => {
  switch (selectedCountry) {
    case CountryCodes.SG:
      return [SINGAPORE_L2.id];
    case CountryCodes.ID:
    case CountryCodes.VN:
    case CountryCodes.TW:
    case CountryCodes.MY:
      return [];
  }
};
